/**
 * `<ViewModeToggle/>` — the tree · outline · split segmented control.
 *
 * Issue #759 (Phase 2, PR-2). The gallery shows three editor surfaces for
 * the same tree — tree canvas (B2.1), full-screen outline (B2.3), and the
 * side-by-side split (B2.4) — reached by the `⤢` toggle affordances. This
 * is the single control that cycles between them; the active surface is held
 * in the shared `treeStore` (`viewMode`).
 *
 * Presentational: it reads the current mode + emits the chosen one. Mint /
 * white / indigo to match the gallery.
 */

import { PALETTE } from "../tree/palette";
import type { ViewMode } from "../tree/treeStore";

const MODES: { mode: ViewMode; label: string }[] = [
  { mode: "tree", label: "Tree" },
  { mode: "outline", label: "Outline" },
  { mode: "split", label: "Split" },
];

export function ViewModeToggle({
  mode,
  onChange,
}: {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
}) {
  return (
    <div
      data-testid="view-mode-toggle"
      role="tablist"
      aria-label="Editor view"
      className="inline-flex items-center rounded-md border p-0.5"
      style={{ borderColor: PALETTE.softBorder, backgroundColor: "#FFFFFF" }}
    >
      {MODES.map(({ mode: m, label }) => {
        const active = m === mode;
        return (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`view-mode-${m}`}
            onClick={() => onChange(m)}
            className="rounded px-3 py-1 text-xs font-medium transition-colors"
            style={{
              backgroundColor: active ? PALETTE.teal : "transparent",
              color: active ? "#FFFFFF" : PALETTE.slate,
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
